import classNames from 'classnames'
import { ArrowLeft, PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { observer } from 'mobx-react-lite'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { fetchUserIfNeeded } from '../../actions/user'
import { useUser } from '../../hooks/useUser'
import { navStore } from '../nav/Nav'
import { NavbarBottomSection } from '../navbar/NavbarBottomSection'
import { ProfileSelect } from '../navbar/ProfileSelect'
import { Tooltip } from '../ui/tooltip'
import { sections as allSections, settingsSections } from './items'
import { NavSection } from './NavSection'
import { NavSubscriptionWarning } from './NavSubscriptionWarning'
import { navV2Store } from './store'

// NavV2 — clean rebuild of the left nav. This is the default nav shell.
//
// Architecture:
//   - A single root container always renders, fixed at the left edge.
//   - `translateX` is the only thing that changes between states.
//   - Pinned → translateX(0), z-10, content area shifted right (AppContainer
//     reads navStore.isPinned and navV2Store.widthPx to compute content width).
//   - Unpinned, not peeking → translateX(-100%), hidden off-screen.
//   - Unpinned, peeking → translateX(0), z-20 overlay on top of content
//     (AppContainer stays 100%; the nav just floats above).
//
// CSS transitions handle the slide. No JS animation orchestration.
//
// We continue to use the legacy `navStore` only for `isPinned`/`open` so
// AppContainer's existing width calc keeps working without modification.
export const NavV2 = observer(({ calloutsCount }: { calloutsCount: number }) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { pathname, search, hash } = useLocation()
    const { user, hasUser, profile, activeProfile, profiles, marketplace } = useUser()

    const hideShell = useMemo(
        () =>
            pathname === '/login' ||
            pathname === '/register' ||
            pathname === '/password/forget' ||
            pathname.startsWith('/password/reset'),
        [pathname],
    )

    useEffect(() => {
        if (marketplace) {
            // @ts-ignore
            window.marketplace = marketplace
        }
    }, [marketplace])

    useEffect(() => {
        dispatch(fetchUserIfNeeded())
    }, [profile])

    useEffect(() => {
        // Don't trap an impersonating admin on /onboard: the keyless account
        // they're viewing would otherwise force-redirect every route back to
        // onboarding, hiding the rest of the app.
        if (user?.impersonating) {
            return
        }
        if (user && user.hasOwnProperty('keys') && !user.keys) {
            navigate('/onboard')
        }
    }, [user])

    const isAdmin = !!user?.is_admin
    const inSettingsArea = isSettingsPath(pathname)
    const currentUrl = `${pathname}${search}${hash}`
    const settingsBackTarget = useMemo(() => {
        if (!inSettingsArea) return null

        try {
            const target = sessionStorage.getItem(SETTINGS_BACK_TARGET_KEY)
            return isUsableSettingsBackTarget(target) ? target : null
        } catch {
            return null
        }
    }, [inSettingsArea, pathname])
    const visibleSections = useMemo(() => {
        const sections = inSettingsArea ? settingsSections : allSections
        return sections
            .filter((s) => (s.visible ? s.visible({ isAdmin, user }) : true))
            .map((s) => ({
                ...s,
                items: s.items.filter((item) =>
                    item.visible ? item.visible({ isAdmin, user }) : true,
                ),
            }))
            .filter((s) => s.items.length > 0)
    }, [inSettingsArea, isAdmin, user])

    useEffect(() => {
        if (hideShell || inSettingsArea) return

        try {
            sessionStorage.setItem(SETTINGS_BACK_TARGET_KEY, currentUrl)
        } catch {}
    }, [currentUrl, hideShell, inSettingsArea])

    useEffect(() => {
        document.documentElement.style.setProperty('--mj-nav-v2-width', navV2Store.widthStyle)
    }, [navV2Store.widthPx])

    const pinned = navStore.isPinned

    // Peek state: true while the user is hovering the left edge hot-zone or
    // the nav itself (when unpinned). Small leave-delay prevents flicker.
    const [peeking, setPeeking] = useState(false)
    const [resizing, setResizing] = useState(false)
    const leaveTimer = useRef<number | undefined>(undefined)
    const resizeStart = useRef<{ x: number; width: number } | null>(null)

    const startPeek = () => {
        if (leaveTimer.current) {
            window.clearTimeout(leaveTimer.current)
            leaveTimer.current = undefined
        }
        setPeeking(true)
    }
    const endPeek = () => {
        if (resizing) return
        if (leaveTimer.current) window.clearTimeout(leaveTimer.current)
        leaveTimer.current = window.setTimeout(() => setPeeking(false), 60)
    }

    // Close any active peek immediately when the user pins (avoids a brief
    // visual jump from "overlay" → "in flow" mid-animation).
    useEffect(() => {
        if (pinned) setPeeking(false)
    }, [pinned])

    const visible = pinned || peeking
    const overlaying = !pinned && peeking
    const overlayInset = 8
    const navWidth = pinned ? navV2Store.widthPx : navV2Store.widthPx - overlayInset * 2

    const startResize = (event: React.MouseEvent<HTMLDivElement>) => {
        event.preventDefault()
        event.stopPropagation()
        if (leaveTimer.current) window.clearTimeout(leaveTimer.current)
        resizeStart.current = { x: event.clientX, width: navV2Store.widthPx }
        setResizing(true)
    }

    useEffect(() => {
        if (!resizing) return

        const onMouseMove = (event: MouseEvent) => {
            const start = resizeStart.current
            if (!start) return
            navV2Store.setWidth(start.width + event.clientX - start.x)
        }
        const onMouseUp = () => {
            resizeStart.current = null
            setResizing(false)
        }

        document.body.style.cursor = 'col-resize'
        document.body.style.userSelect = 'none'
        window.addEventListener('mousemove', onMouseMove)
        window.addEventListener('mouseup', onMouseUp)

        return () => {
            document.body.style.cursor = ''
            document.body.style.userSelect = ''
            window.removeEventListener('mousemove', onMouseMove)
            window.removeEventListener('mouseup', onMouseUp)
        }
    }, [resizing])

    if (hideShell || !hasUser) {
        // For auth pages we still want the existing legacy behavior of
        // hiding the nav entirely; return nothing.
        return null
    }

    return (
        <>
            {/* Edge hot-zone — only present when unpinned. A thin vertical
                strip that triggers a peek when the cursor enters it. */}
            {!pinned && (
                <div className="fixed top-0 left-0 w-2 h-screen z-30" onMouseEnter={startPeek} />
            )}

            {/* Floating "open" button shown when unpinned and not peeking,
                so users have a visible affordance to re-open the nav. */}
            {!pinned && !peeking && <FloatingOpenButton />}

            {/* The nav itself — single container, slides via transform.
                When overlaying (peeking while unpinned), it morphs into a
                floating card: inset from the edges, rounded, drop-shadow.
                When pinned, it's flush to the edges and full-height. */}
            <div
                onMouseEnter={!pinned ? startPeek : undefined}
                onMouseLeave={!pinned ? endPeek : undefined}
                style={{
                    width: navWidth,
                    // When hidden, slide an extra 8px past the left edge so
                    // the inset card variant doesn't leave a sliver showing.
                    transform: visible
                        ? 'translateX(0)'
                        : overlaying
                          ? 'translateX(calc(-100% - 8px))'
                          : 'translateX(-100%)',
                }}
                className={classNames(
                    'mj-nav fixed left-0 top-0 flex flex-col',
                    'transition-all duration-100 ease-out',
                    'will-change-transform',
                    overlaying
                        ? // Overlay card variant — equal 8px gap top/left/bottom.
                          // White so it pops against page content as a card.
                          [
                              'bg-white',
                              'm-2 h-[calc(100vh-1rem)]',
                              'rounded-lg border border-mjGray-600 shadow-xl',
                              'z-20 overflow-hidden',
                          ]
                        : // Flush-pinned variant — subtle off-white tints the
                          // sidebar against the white page content.
                          [
                              'bg-[#FBFBFB]',
                              'm-0 h-screen',
                              'rounded-none border-r border-mjGray-600',
                              'z-10',
                          ],
                )}
            >
                <div
                    aria-hidden
                    onMouseDown={startResize}
                    className={classNames(
                        'absolute top-0 -right-1 h-full w-2 cursor-col-resize z-30',
                        'after:absolute after:right-1 after:top-0 after:h-full after:w-px',
                        'after:bg-transparent hover:after:bg-mjBlue-400',
                        resizing && 'after:bg-mjBlue-400',
                    )}
                />
                <LogoBar settingsMode={inSettingsArea} settingsBackTarget={settingsBackTarget} />
                <nav className="flex-1 overflow-y-auto py-1">
                    {visibleSections.map((s) => (
                        <NavSection key={s.key} section={s} />
                    ))}
                </nav>
                {/* Subscription / trial warning lives here in v2 instead of
                    at the top of the page. Renders nothing when the user
                    has a healthy active subscription. */}
                <NavSubscriptionWarning />
                <div className={classNames('h-12', pinned ? 'bg-[#FBFBFB]' : 'bg-white')}>
                    <NavbarBottomSection
                        activeProfile={activeProfile ?? undefined}
                        profiles={profiles}
                    />
                </div>
            </div>
        </>
    )
})

// Logo + pin toggle row.
const SETTINGS_PATHS = ['/profile-settings', '/settings', '/keys', '/api-keys']
const SETTINGS_BACK_TARGET_KEY = 'mj_nav_v2_settings_back_target'

const isSettingsPath = (pathname: string): boolean => {
    return (
        SETTINGS_PATHS.includes(pathname) ||
        pathname === '/billing' ||
        pathname.startsWith('/billing/')
    )
}

const isUsableSettingsBackTarget = (target: string | null): target is string => {
    if (!target) return false

    try {
        const url = new URL(target, window.location.origin)
        return url.origin === window.location.origin && !isSettingsPath(url.pathname)
    } catch {
        return false
    }
}

const LogoBar = observer(
    ({
        settingsMode = false,
        settingsBackTarget = null,
    }: {
        settingsMode?: boolean
        settingsBackTarget?: string | null
    }) => {
        const pinned = navStore.isPinned
        const navigate = useNavigate()
        const { search } = useLocation()
        const [profileMenuOpen, setProfileMenuOpen] = useState(false)
        const onTogglePin = () => {
            if (pinned) {
                navStore.setState('hidden')
                navStore.setOpen(false)
            } else {
                navStore.setState('pinned')
                navStore.setOpen(true)
            }
        }
        const onSettingsBack = () => {
            navigate(settingsBackTarget ?? `/dashboard${search}`)
        }

        return (
            <div
                className={classNames(
                    'pl-2 pr-2 flex items-center gap-1.5 border-b border-mjGray-600 transition-colors duration-75',
                    pinned ? 'h-[49px]' : 'h-[41px]',
                    !settingsMode && 'hover:bg-[#E9ECEF]',
                    !settingsMode && profileMenuOpen && 'bg-[#E9ECEF]',
                )}
            >
                {settingsMode ? (
                    <>
                        <Tooltip hoverOpenDelay={300} content={<span>Back</span>}>
                            <button
                                type="button"
                                onClick={onSettingsBack}
                                aria-label="Back"
                                className={classNames(
                                    'inline-flex items-center justify-center w-7 h-7 rounded shrink-0',
                                    'text-mjDark-600',
                                    'transform-gpu transition-[background-color,color,transform] duration-150 ease-out',
                                    'hover:-translate-y-0.5 hover:bg-mjGray-300 hover:text-mjDark-1000',
                                    'active:translate-y-0 active:scale-[0.96]',
                                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-mjBlue-300',
                                )}
                            >
                                <span className="inline-flex animate-nav-pin-pop">
                                    <ArrowLeft size={18} />
                                </span>
                            </button>
                        </Tooltip>
                        <div className="min-w-0 flex-1 truncate text-[16px] font-semibold text-[#0E2947]">
                            Settings
                        </div>
                    </>
                ) : (
                    <div className="min-w-0 flex-1 self-stretch [&>div]:!px-0 [&>div]:!py-0">
                        <ProfileSelect
                            id="nav-v2-profile-select"
                            onOpenChange={setProfileMenuOpen}
                        />
                    </div>
                )}
                <Tooltip
                    hoverOpenDelay={300}
                    content={<span>{pinned ? 'Collapse sidebar' : 'Pin sidebar'}</span>}
                >
                    <button
                        id="nav-v2-pin"
                        type="button"
                        onClick={onTogglePin}
                        aria-label={pinned ? 'Collapse sidebar' : 'Pin sidebar'}
                        className={classNames(
                            'inline-flex items-center justify-center w-7 h-7 rounded shrink-0',
                            'text-mjDark-600',
                            'transform-gpu transition-[background-color,color,transform] duration-150 ease-out',
                            'hover:-translate-y-0.5 hover:bg-mjGray-300 hover:text-mjDark-1000',
                            'active:translate-y-0 active:scale-[0.96]',
                            'focus:outline-none focus-visible:ring-2 focus-visible:ring-mjBlue-300',
                        )}
                    >
                        <span
                            key={pinned ? 'close' : 'open'}
                            className="inline-flex animate-nav-pin-pop"
                        >
                            {pinned ? <PanelLeftClose size={18} /> : <PanelLeftOpen size={18} />}
                        </span>
                    </button>
                </Tooltip>
            </div>
        )
    },
)

// A small button anchored to the top-left of the content area when the nav
// is fully collapsed, so the user always has an explicit way back in.
const FloatingOpenButton = () => {
    const onClick = () => {
        navStore.setState('pinned')
        navStore.setOpen(true)
    }
    return (
        <Tooltip hoverOpenDelay={300} content={<span>Pin sidebar</span>}>
            <button
                type="button"
                onClick={onClick}
                aria-label="Pin sidebar"
                className={classNames(
                    'fixed top-[8.5px] left-3 z-20 w-8 h-8 rounded',
                    'inline-flex items-center justify-center',
                    'bg-white border border-mjGray-600 shadow-sm',
                    'text-mjDark-600',
                    'transform-gpu transition-[background-color,color,box-shadow,transform] duration-150 ease-out',
                    'hover:-translate-y-0.5 hover:bg-mjGray-300 hover:text-mjDark-1000 hover:shadow-md',
                    'active:translate-y-0 active:scale-[0.96] active:shadow-sm',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-mjBlue-300',
                )}
            >
                <span key="floating-open" className="inline-flex animate-nav-pin-pop">
                    <PanelLeftOpen size={18} />
                </span>
            </button>
        </Tooltip>
    )
}
